import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useEffect } from 'react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import {
  applyTheme,
  isThemePreference,
  readCachedPreference,
  resolveTheme,
  watchSystemTheme,
  writeCachedPreference,
  type ResolvedTheme,
  type ThemePreference,
} from '@/lib/theme'

/**
 * Estado de interface que atravessa telas: a sidebar e o TEMA.
 *
 * O tema tem dois campos porque sao duas perguntas diferentes:
 *
 *   themePreference  o que o ALUNO escolheu — 'light', 'dark' ou `null` ("nunca escolhi")
 *   resolvedTheme    o que a TELA esta mostrando agora; com preferencia nula, e o sistema
 *
 * A fonte da verdade da preferencia e o PERFIL (`users.theme_preference`), nao este store:
 * quem entra de outro computador leva a escolha junto. O `localStorage` guarda uma copia
 * com um proposito unico — pintar a tela certa antes de o perfil chegar do servidor.
 */

interface UiState {
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebar: () => void
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: (open: boolean) => void
  toggleMobileSidebar: () => void

  themePreference: ThemePreference
  resolvedTheme: ResolvedTheme
  /** Escolha do aluno: aplica na hora, guarda no cache e salva no perfil. */
  setThemePreference: (preference: ThemePreference) => void
  /** Inverte o que esta na tela e vira escolha explicita. */
  toggleTheme: () => void
  /** Adota o que veio do perfil, sem devolver nada ao servidor. */
  adoptProfilePreference: (preference: ThemePreference) => void
  /** Reaplica o tema quando o sistema muda — so vale com preferencia nula. */
  syncWithSystem: () => void
}

const initialPreference = readCachedPreference()

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      mobileSidebarOpen: false,
      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),

      themePreference: initialPreference,
      resolvedTheme: resolveTheme(initialPreference),

      setThemePreference: (preference) => {
        const resolved = resolveTheme(preference)
        applyTheme(resolved)
        writeCachedPreference(preference)
        set({ themePreference: preference, resolvedTheme: resolved })
        persistPreferenceInProfile(preference)
      },

      toggleTheme: () => {
        get().setThemePreference(get().resolvedTheme === 'dark' ? 'light' : 'dark')
      },

      adoptProfilePreference: (preference) => {
        if (get().themePreference === preference) return

        const resolved = resolveTheme(preference)
        applyTheme(resolved)
        writeCachedPreference(preference)
        set({ themePreference: preference, resolvedTheme: resolved })
      },

      syncWithSystem: () => {
        // Preferencia explicita ignora o sistema: quem escolheu "claro" nao quer a tela
        // virando sozinha quando o computador troca de modo.
        if (get().themePreference !== null) return

        const resolved = resolveTheme(null)
        applyTheme(resolved)
        set({ resolvedTheme: resolved })
      },
    }),
    {
      name: 'medhunter-ui',
      version: 3,
      // So a sidebar. O tema vive no perfil (e no cache proprio de `lib/theme.ts`), e
      // `mobileSidebarOpen` e estado de momento — persistir abria o menu do celular
      // sozinho na visita seguinte.
      partialize: (state) => ({ sidebarCollapsed: state.sidebarCollapsed }),
      migrate: (persistedState: unknown, version: number) => {
        const state = (persistedState ?? {}) as Record<string, unknown>
        if (version < 3) {
          // Ate a v2 o tema morava aqui e NASCIA 'dark' para todo mundo — era default, nao
          // escolha, ainda mais porque o botao nao funcionava. Levar esse valor adiante
          // deixaria a base inteira com preferencia explicita e o padrao do sistema nunca
          // valeria; por isso ele e descartado e a escolha recomeca vazia.
          const { theme: _theme, mobileSidebarOpen: _open, ...rest } = state
          return rest
        }
        return state
      },
    }
  )
)

/**
 * Salva a escolha no perfil. Silencioso de proposito: o tema ja mudou na tela e no cache, e
 * uma falha de rede aqui nao pode virar um erro no meio da navegacao — na proxima carga o
 * perfil manda, que e o comportamento correto para "nao consegui salvar".
 */
function persistPreferenceInProfile(preference: ThemePreference) {
  const { isAuthenticated, user, setUser } = useAuthStore.getState()
  // Fora da sessao (login, paginas publicas) nao ha perfil onde gravar: fica so o cache.
  if (!isAuthenticated || !user) return
  if ((user.theme_preference ?? null) === preference) return

  api.users
    .updateProfile({ theme_preference: preference })
    .then((response) => {
      if (response?.user) setUser(response.user)
    })
    .catch(() => {
      /* ver o docblock: o cache local segura a escolha ate a proxima carga */
    })
}

/**
 * Liga o tema ao ciclo de vida da tela: aplica o resolvido, adota o que o perfil trouxer e
 * acompanha o sistema enquanto o aluno nao escolher.
 */
export const useThemeInit = () => {
  const resolvedTheme = useUiStore((state) => state.resolvedTheme)
  const themePreference = useUiStore((state) => state.themePreference)
  const adoptProfilePreference = useUiStore((state) => state.adoptProfilePreference)
  const syncWithSystem = useUiStore((state) => state.syncWithSystem)
  const profilePreference = useAuthStore((state) => state.user?.theme_preference ?? null)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  // O perfil e a fonte da verdade: ao entrar (ou ao trocar de conta) a escolha salva vence
  // o cache local. `null` no perfil e uma resposta legitima — volta a seguir o sistema.
  useEffect(() => {
    if (!isAuthenticated) return
    adoptProfilePreference(isThemePreference(profilePreference) ? profilePreference : null)
  }, [isAuthenticated, profilePreference, adoptProfilePreference])

  useEffect(() => {
    if (themePreference !== null) return
    return watchSystemTheme(syncWithSystem)
  }, [themePreference, syncWithSystem])
}
